import React, { Fragment, useEffect, useRef, useState } from 'react'
import { FormikProps } from 'formik'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { Filter, FilterComponent } from '../../components/_WMS/FilterComponent'
import { filterOptions } from './config/filterOptions'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { usePagination } from '../../hooks/usePagination'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { GenericOrderBy } from '../../components/_EOS/Table'
import { deleteOrderOrigin, getOrderOriginList } from './config/api'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Status } from '../../hooks/useFetchState'
import { getColumnsConfig } from './config/tableColumns'
import { useModal } from '../../hooks/useModal'
import { Modal } from '../../components/_EOS/Modal'
import { Delete, Edit, Visibility } from '@mui/icons-material'
import { ViewOrderOriginModal } from './modal/ViewOrderOriginModal'
import { OrderOrigin, OrderOriginInfoDto } from '../../interfaces/OrderOrigin/OrderOrigin'
import { CreateEditOrderOriginModal } from './modal/CreateEditOrderOriginModal'

interface OrderOriginPageProps {}

export const OrderOriginPage: React.FC<OrderOriginPageProps> = () => {
  const { callApi, state } = useApi<ApiResponse<OrderOriginInfoDto>>()
  const { callApi: requestDeleteOrderOrigin, state: deleteState } = useApi()
  const [selectedFilters, setSelectedFilters] = useState<Filter[]>([])
  const { isActive, message, openSnackBar, isSuccess } = useSnackbar()
  const pagination = usePagination(5)

  const [orderBy, setOrderBy] = useState<GenericOrderBy<OrderOriginInfoDto>>({
    field: 'origin' as keyof OrderOriginInfoDto,
    direction: 'asc'
  })

  const columnsConfig = getColumnsConfig()

  const handleSorting = (field: keyof OrderOriginInfoDto, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePagination = (page: number, pageSize: number) => {
    const auxPage = pagination.pageSize !== pageSize ? 0 : page
    requestData(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestData = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    const filtersToUse = filters !== undefined ? filters : selectedFilters
    const pageToUse = page !== undefined ? page : pagination.page
    const pageSizeToUse =
      pageSize !== undefined ? pageSize : pagination.pageSize

    callApi(
      getOrderOriginList(filtersToUse, pageToUse, pageSizeToUse, orderBy)
    ).catch((error) => {
      openSnackBar(error, false)
    })
  }

  const resetAndFetchData = (filters?: Filter[]) => {
    pagination.setPagination(0, pagination.pageSize)
    requestData(filters, 0, pagination.pageSize)
  }

  // Refrescar solo cuando cambia el ordenamiento
  useEffect(() => {
    resetAndFetchData(selectedFilters)
  }, [orderBy])

  const createModal = useModal('Crear Canal de Venta')
  const editModal = useModal<OrderOriginInfoDto>('Editar Canal de Venta')
  const viewModal = useModal<number>('Detalle')
  const deleteModal = useModal<OrderOriginInfoDto>('Confirmar Eliminación')

  const createFormRef = useRef<FormikProps<OrderOrigin>>(null)
  const editFormRef = useRef<FormikProps<OrderOrigin>>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isEditingSubmitting, setIsEditingSubmitting] = useState(false)

  const handleCreateSubmit = async () => {
    setIsCreating(true)
    await createFormRef.current?.submitForm()
    setIsCreating(false)
  }

  const handleEditSubmit = async () => {
    setIsEditingSubmitting(true)
    await editFormRef.current?.submitForm()
    setIsEditingSubmitting(false)
  }

  const handleDelete = (OrderOriginId: number) => {
    requestDeleteOrderOrigin(deleteOrderOrigin(OrderOriginId))
      .then(() => {
        openSnackBar('Canal de Venta eliminado exitosamente', true)
        deleteModal.closeModal()
        resetAndFetchData()
      })
      .catch((error) => {
        openSnackBar(error.message || 'Error al eliminar', false)
      })
  }

  const handleCreateClick = () => {
    createModal.openModal('md')
  }

  return (
    <Fragment>
      <StandardPage pageTitle=''>
        <FilterComponent
          includeAddButton={true}
          options={filterOptions}
          onSearchClick={resetAndFetchData}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          includeSearchOnClean={true}
          onAddButtonClick={handleCreateClick}
          createButtonTooltip='Crear Canal de Venta'
        />
        <GenericTableV2<OrderOriginInfoDto>
          data={state.data?.items || []}
          columns={columnsConfig}
          keyField='orderOriginId'
          loading={state.status === Status.Loading}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePagination
          }}
          sorting={{
            field: orderBy.field as keyof OrderOriginInfoDto,
            direction: orderBy.direction,
            onSortChange: handleSorting
          }}
          actions={[
            {
              key: 'edit',
              label: 'Editar',
              onClick: (item) => editModal.openModal('md', item),
              icon: <Edit />
            },
            {
              key: 'view',
              label: 'Ver Detalle',
              onClick: (row) => viewModal.openModal('md', row.orderOriginId),
              icon: <Visibility />
            },

            {
              key: 'delete',
              label: 'Eliminar',
              onClick: (item) => deleteModal.openModal('sm', item),
              icon: <Delete />
            }
          ]}
        />
      </StandardPage>

      <Modal
        open={viewModal.isOpen}
        maxWidth={viewModal.size}
        title={viewModal.title}
        onClose={viewModal.closeModal}
        cancelButton={{
          action: viewModal.closeModal,
          title: 'Cancelar'
        }}
      >
        <ViewOrderOriginModal
          orderOriginId={viewModal?.extendedProps || 0}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={createModal.isOpen}
        maxWidth={createModal.size}
        title={createModal.title}
        onClose={createModal.closeModal}
        successButton={{
          action: handleCreateSubmit,
          title: 'Guardar',
          disabled: isCreating
        }}
        cancelButton={{
          action: createModal.closeModal,
          title: 'Cancelar',
          disabled: isCreating
        }}
        loading={isCreating}
      >
        <CreateEditOrderOriginModal
          ref={createFormRef}
          isEditing={false}
          closeModal={createModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={editModal.isOpen}
        maxWidth={editModal.size}
        title={editModal.title}
        onClose={editModal.closeModal}
        successButton={{
          action: handleEditSubmit,
          title: 'Guardar',
          disabled: isEditingSubmitting
        }}
        cancelButton={{
          action: editModal.closeModal,
          title: 'Cancelar',
          disabled: isEditingSubmitting
        }}
        loading={isEditingSubmitting}
      >
        <CreateEditOrderOriginModal
          ref={editFormRef}
          orderOrigin={editModal.extendedProps}
          isEditing={true}
          closeModal={editModal.closeModal}
          onSuccess={requestData}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={deleteModal.isOpen}
        maxWidth={deleteModal.size}
        title={deleteModal.title}
        onClose={deleteModal.closeModal}
        successButton={{
          action: () => handleDelete(deleteModal.extendedProps?.orderOriginId || 0),
          title: 'Confirmar',
          disabled: deleteState.status === Status.Loading
        }}
        cancelButton={{
          action: () => deleteModal.closeModal(),
          title: 'Cancelar',
          disabled: deleteState.status === Status.Loading
        }}
      >
        <span>
          ¿Desea eliminar el Canal de Venta:{' '}
          <strong>{deleteModal.extendedProps?.origin}</strong>?
        </span>
      </Modal>

      <SnackBar
        message={message}
        status={isSuccess ? 'success' : 'error'}
        open={isActive}
      />
    </Fragment>
  )
}
